import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  InputGroup,
  Label,
  Row,
} from "reactstrap";
import { AvField, AvForm } from "availity-reactstrap-validation";
import PropTypes from "prop-types";
import Flatpickr from "react-flatpickr";
import {
  getLegalEntity,
  getLegalEntityNames,
} from "../../../../../helpers/jwt_helper";
import CustomSelect from "../../../../../components/Common/CustomSelect";
import {
  getCurrentDay,
  getDefaultValueForSelect,
} from "../../../../../helpers/utils";
import PreloaderData from "../../../../../components/PreloaderData";
import styled from "styled-components";

const ContractorInformationForm = ({
  data,
  contactData,
  history,
  addContractor,
  updateContractorProfile,
  contractorId,
  loader,
  permissions,
}) => {
  const [signDate, setSignDate] = useState(data?.date_of_signature);
  const [expDate, setExpDate] = useState(data?.expiration);
  const [legalEntity, setLegalEntity] = useState({ isLoading: true, data: [] });

  useEffect(async () => {
    try {
      let response = await getLegalEntityNames();
      response = response.map(i => ({
        label: i.counterparty_name,
        value: i.id,
      }));
      setLegalEntity({ isLoading: false, data: response });
    } catch (e) {
      setLegalEntity({ isLoading: false, data: [] });
      console.log("ContractorInformationForm error getting Legal entity!");
    }
  }, []);

  useEffect(() => {
    if (data?.date_of_signature) {
      setSignDate(data?.date_of_signature);
    } else if (data?.expiration) {
      setExpDate(data?.expiration);
    }
  }, [data]);

  const formSubmit = (e, errors, params) => {
    if (errors.length === 0) {
      let contractor = {
        ...params,
        inner_contractor: params.inner_contractor.value,
      };

      if (signDate) contractor.date_of_signature = signDate;
      if (expDate) contractor.expiration = expDate;

      if (contractorId) {
        updateContractorProfile(contractorId, contractor, contactData);
      } else {
        addContractor(contractor, contactData);
      }
      history.push("/contractors");
    }
  };

  return (
    <React.Fragment>
      {!loader ? (
        <AvForm
          className="needs-validation fade-in-custom-animate"
          onSubmit={formSubmit}
        >
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-3">Contract</h4>
                  <Row className="mt-2">
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Contract number:</Label>
                        <AvField
                          name="contract_number"
                          placeholder="Contract number"
                          value={data?.contract_number || ""}
                          type="text"
                          errorMessage="Contract number field is required!"
                          className="form-control"
                          validate={{ required: { value: true } }}
                          // id="validationCustom01"
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Date of signature:</Label>
                        <InputGroup>
                          <Flatpickr
                            name="date_of_signature"
                            className="form-control d-block bg-white"
                            placeholder="Select date of signature"
                            value={data?.date_of_signature || signDate}
                            options={{
                              dateFormat: "Y-m-d",
                            }}
                            onChange={(e, dateStr) => setSignDate(dateStr)}
                            //validate={{ required: { value: true } }}
                          />
                        </InputGroup>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Expiration date:</Label>
                        <InputGroup>
                          <Flatpickr
                            name="expiration"
                            className={`form-control d-block ${
                              signDate ? "bg-white" : ""
                            }`}
                            placeholder="Select expiration date"
                            value={data?.expiration || ""}
                            options={{
                              dateFormat: "Y-m-d",
                              minDate: data?.date_of_signature || signDate,
                            }}
                            disabled={!signDate}
                            onChange={(e, dateStr) => setExpDate(dateStr)}
                            //validate={{ required: { value: true } }}
                          />
                        </InputGroup>
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="validationCustom02">
                          Legal entity:
                        </Label>
                        <CustomSelect
                          name="inner_contractor"
                          options={legalEntity.data}
                          placeholder="Select legal entity..."
                          defaultValue={getDefaultValueForSelect(
                            data?.inner_contractor?.id
                          )}
                          // defaultValue={[]}
                          classNamePrefix="select2-selection"
                          errorMessage="Legal entity field is required!"
                          isFormSubmit={false}
                          isLoading={legalEntity.isLoading}
                        />
                      </FormGroup>
                    </Col>
                    {contractorId && (
                      <Col sm={12} style={{ textAlign: "center" }}>
                        <Label htmlFor="validationCustom02">Balance:</Label>
                        <BalanceBlock balance={data?.balance}>
                          <span>
                            {data?.balance} {data?.balance_currency}
                          </span>
                        </BalanceBlock>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-3">Contractor information</h4>
                  <Row className="mt-2">
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Counterparty name:</Label>
                        <AvField
                          name="counterparty_name"
                          placeholder="Counterparty name"
                          value={data?.counterparty_name || ""}
                          type="text"
                          errorMessage="Counterparty name field is required!"
                          className="form-control"
                          validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Legal address:</Label>
                        <AvField
                          name="legal_address"
                          placeholder="Legal address"
                          value={data?.legal_address || ""}
                          type="text"
                          //errorMessage="Legal address field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Postal address:</Label>
                        <AvField
                          name="postal_address"
                          placeholder="Postal address"
                          value={data?.postal_address || ""}
                          type="text"
                          //errorMessage="Postal address field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Settlement account:</Label>
                        <AvField
                          name="settlement_account"
                          placeholder="Settlement account"
                          value={data?.settlement_account || ""}
                          type="text"
                          //errorMessage="Settlement account field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Correspondent account:</Label>
                        <AvField
                          name="correspondent_account"
                          placeholder="Correspondent account"
                          value={data?.correspondent_account || ""}
                          type="text"
                          //errorMessage="Correspondent account field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Name of bank:</Label>
                        <AvField
                          name="bank_name"
                          placeholder="Bank name"
                          value={data?.bank_name || ""}
                          type="text"
                          //errorMessage="Bank name field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Bank address:</Label>
                        <AvField
                          name="bank_address"
                          placeholder="Bank address"
                          value={data?.bank_address || ""}
                          type="text"
                          //errorMessage="Bank address field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">SWIFT:</Label>
                        <AvField
                          name="swift"
                          placeholder="SWIFT"
                          value={data?.swift || ""}
                          type="text"
                          //errorMessage="SWIFT field is required!"
                          className="form-control"
                          validate={{
                            minLength: {
                              value: 8,
                              errorMessage:
                                "Please enter at least 8 characters.",
                            },
                            maxLength: {
                              value: 11,
                              errorMessage:
                                "Please enter no more than 11 characters.",
                            },
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">
                          CPP (Classifier of Industrial Enterprises):
                        </Label>
                        <AvField
                          name="cpp"
                          placeholder="CPP"
                          value={data?.cpp || ""}
                          type="text"
                          //errorMessage="CPP field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">
                          INN (Taxpayer Identification Number):
                        </Label>
                        <AvField
                          name="inn"
                          placeholder="INN"
                          value={data?.inn || ""}
                          type="text"
                          //errorMessage="INN field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">
                          OGRN (Principal State Registration Number):
                        </Label>
                        <AvField
                          name="ogrn"
                          placeholder="OGRN"
                          value={data?.ogrn || ""}
                          type="text"
                          //errorMessage="OGRN field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">BIC (Bank Identifier Code):</Label>
                        <AvField
                          name="bic"
                          placeholder="BIC"
                          value={data?.bic || ""}
                          type="text"
                          //errorMessage="BIC field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">
                          OKPO (Classifier of Enterprises and Organizations):
                        </Label>
                        <AvField
                          name="okpo"
                          placeholder="OKPO"
                          value={data?.okpo || ""}
                          type="text"
                          //errorMessage="OKPO field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Signer:</Label>
                        <AvField
                          name="signer"
                          placeholder="Signer"
                          value={data?.signer || ""}
                          type="text"
                          //errorMessage="Signer field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Position:</Label>
                        <AvField
                          name="position"
                          placeholder="Position"
                          value={data?.position || ""}
                          type="text"
                          //errorMessage="Position field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Payment description:</Label>
                        <AvField
                          name="payment_description"
                          placeholder="Payment description"
                          value={data?.payment_description || ""}
                          type="text"
                          //errorMessage="Position field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-3">Tax</h4>
                  <Row className="mt-2">
                    <Col lg="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Tax type:</Label>
                        <AvField
                          name="tax_type"
                          placeholder="Tax type"
                          value={data?.tax_type || ""}
                          type="text"
                          //errorMessage="Tax type field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Tax rate:</Label>
                        <AvField
                          name="tax_rate"
                          placeholder="Tax rate"
                          value={data?.tax_rate || ""}
                          type="number"
                          //errorMessage="Tax rate field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="">Type:</Label>
                        <AvField
                          name="type"
                          placeholder="Type"
                          value={data?.type || ""}
                          //errorMessage="Type field is required!"
                          className="form-control"
                          //validate={{ required: { value: true } }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="justify-content-center">
            {permissions?.["change_contractmodel"] && (
              <Col className="col-12" align="right">
                <Button
                  style={{ margin: "25px 0" }}
                  type="submit"
                  color="success"
                >
                  Save changes
                </Button>
              </Col>
            )}
          </Row>
        </AvForm>
      ) : (
        <PreloaderData />
      )}
    </React.Fragment>
  );
};

const BalanceBlock = styled.div`
  color: ${props => (props?.balance < 0 ? "rgb(244,106,106)" : "inherit")};
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;

  span {
    font-size: 1.5rem;
  }
`;

ContractorInformationForm.propTypes = {
  data: PropTypes.object,
  contactData: PropTypes.shape({
    results: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  history: PropTypes.object.isRequired,
  addContractor: PropTypes.func.isRequired,
  updateContractorProfile: PropTypes.func,
  contractorId: PropTypes.string,
  loader: PropTypes.bool,
  permissions: PropTypes.any,
};

export default ContractorInformationForm;
